import {ExpertSystemEquipmentType} from '@domain/expert-system'
import {HierarchyNodeMetadata} from '@domain/shared'
import {ExpertSystemHierarchyNodeType} from '@hconnect/common/types'
import {omit} from 'lodash'
import {generatePath} from 'react-router'

import {
  AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE,
  COUNTRY_EXPERT_SYSTEM_USAGE_RATE_ROUTE,
  PLANT_EXPERT_SYSTEM_USAGE_RATE_ROUTE,
  REGION_EXPERT_SYSTEM_USAGE_RATE_ROUTE
} from '../consts'
import {HierarchyIdParams, HierarchyNode, HierarchyTableNode} from '../types'

export function createNewTreeWithIsAllowedProperty(node: HierarchyNodeMetadata): HierarchyNode {
  const newNode: HierarchyNode = {...omit(node, 'children'), isAllowed: false, children: []}
  newNode.children = node.children.map((child) => createNewTreeWithIsAllowedProperty(child))
  return newNode
}

export function setAllowedNodes(
  tree: HierarchyNodeMetadata,
  allowedLocations: string[]
): HierarchyNode {
  const idSet = new Set(allowedLocations)
  const hierarchyTree = createNewTreeWithIsAllowedProperty(tree)

  function traverse(node: HierarchyNode) {
    if (idSet.has(node.id)) {
      node.isAllowed = true
      setAncestorsAllowed(hierarchyTree, node)
      setChildrenAllowed(node)
    }

    node.children.forEach((child) => traverse(child))
  }

  function setAncestorsAllowed(root: HierarchyNode, node: HierarchyNode) {
    if (!root) return false

    if (root === node) {
      root.isAllowed = true
      return true
    }

    for (const child of root.children) {
      const result = setAncestorsAllowed(child, node)
      if (result) {
        child.isAllowed = true
        return true
      }
    }

    return false
  }

  function setChildrenAllowed(node) {
    if (!node) return
    node.children.forEach((child) => {
      child.isAllowed = true
      setChildrenAllowed(child)
    })
  }

  traverse(hierarchyTree)
  return {
    ...hierarchyTree,
    isAllowed: hierarchyTree.children.some((child) => child.isAllowed)
  }
}

export function getHierarchyNodes(
  nodes: HierarchyTableNode[],
  type: ExpertSystemHierarchyNodeType
): HierarchyTableNode[] {
  return nodes.flatMap((node) => {
    if (node.type === type) {
      return [node]
    }
    return node.subItems ? getHierarchyNodes(node.subItems, type) : []
  })
}

export function getDisplayLevel(
  params: HierarchyIdParams,
  equipmentType: ExpertSystemEquipmentType
): ExpertSystemHierarchyNodeType {
  if (params.plantId)
    return equipmentType === ExpertSystemEquipmentType.Kiln
      ? ExpertSystemHierarchyNodeType.KilnKpi
      : ExpertSystemHierarchyNodeType.MillKpi
  if (params.countryId) return ExpertSystemHierarchyNodeType.Plant
  if (params.regionId) return ExpertSystemHierarchyNodeType.Country
  if (params.areaId) return ExpertSystemHierarchyNodeType.Region
  return ExpertSystemHierarchyNodeType.Area
}

export function getHierarchyItemNodes(
  data: HierarchyTableNode[],
  params: HierarchyIdParams,
  equipmentType: ExpertSystemEquipmentType
): HierarchyTableNode[] {
  const level = getDisplayLevel(params, equipmentType)

  switch (level) {
    case ExpertSystemHierarchyNodeType.Area:
      return data
    case ExpertSystemHierarchyNodeType.Region: {
      const area = data.find((n) => n.id === params.areaId)
      return area?.subItems || []
    }
    case ExpertSystemHierarchyNodeType.Country: {
      const area = data.find((n) => n.id === params.areaId)
      const region = area?.subItems?.find((n) => n.id === params.regionId)
      return region?.subItems || []
    }
    case ExpertSystemHierarchyNodeType.Plant: {
      const area = data.find((n) => n.id === params.areaId)
      const region = area?.subItems?.find((n) => n.id === params.regionId)
      const country = region?.subItems?.find((n) => n.id === params.countryId)
      return country?.subItems || []
    }
    case ExpertSystemHierarchyNodeType.KilnKpi: {
      const area = data.find((n) => n.id === params.areaId)
      const region = area?.subItems?.find((n) => n.id === params.regionId)
      const country = region?.subItems?.find((n) => n.id === params.countryId)
      const plant = country?.subItems?.find((n) => n.id === params.plantId)
      return (
        plant?.subItems?.filter(({type}) => type === ExpertSystemHierarchyNodeType.KilnKpi) || []
      )
    }
    case ExpertSystemHierarchyNodeType.MillKpi: {
      const area = data.find((n) => n.id === params.areaId)
      const region = area?.subItems?.find((n) => n.id === params.regionId)
      const country = region?.subItems?.find((n) => n.id === params.countryId)
      const plant = country?.subItems?.find((n) => n.id === params.plantId)
      return (
        plant?.subItems?.filter(({type}) => type === ExpertSystemHierarchyNodeType.MillKpi) || []
      )
    }
    default:
      return []
  }
}

export function buildExpertSystemUsageRatePath(
  params: HierarchyIdParams,
  displayLevel: ExpertSystemHierarchyNodeType,
  id: string
): string {
  const {areaId, regionId, countryId} = params

  switch (displayLevel) {
    case ExpertSystemHierarchyNodeType.Area:
      return generatePath(AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE, {areaId: id})

    case ExpertSystemHierarchyNodeType.Region:
      if (!areaId) return AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE
      return generatePath(REGION_EXPERT_SYSTEM_USAGE_RATE_ROUTE, {areaId, regionId: id})

    case ExpertSystemHierarchyNodeType.Country:
      if (!areaId || !regionId) return AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE
      return generatePath(COUNTRY_EXPERT_SYSTEM_USAGE_RATE_ROUTE, {areaId, regionId, countryId: id})

    case ExpertSystemHierarchyNodeType.Plant:
      if (!areaId || !regionId || !countryId) return AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE
      return generatePath(PLANT_EXPERT_SYSTEM_USAGE_RATE_ROUTE, {
        areaId,
        regionId,
        countryId,
        plantId: id
      })

    default:
      return AREA_EXPERT_SYSTEM_USAGE_RATE_ROUTE
  }
}

export const isKilnOrMillLevel = (hierarchyType: ExpertSystemHierarchyNodeType) =>
  hierarchyType === ExpertSystemHierarchyNodeType.KilnKpi ||
  hierarchyType === ExpertSystemHierarchyNodeType.MillKpi
